import { Component, Element, EventEmitter, State, h, Prop, Watch, Listen, Event, Method } from '@stencil/core';
import { focusElement, runAfterNextFrameRender } from '../../utils/functions';
import {
  getInternalFormatFromDate,
  getDateFromInternalFormat,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  isBetweenDate,
  getFirstDayOfMonthWithDate,
  isBeforeDate,
  isAfterDate,
  getLastDayOfMonthWithDate,
  isSameDate,
  WEEKDAY_NAMES,
  MONTH_NAMES,
} from '../../utils/dates';
import { KEY_CODES } from '../../utils/variables';

const ARROW_KEY_CODES = [KEY_CODES.ARROW_LEFT, KEY_CODES.ARROW_UP, KEY_CODES.ARROW_RIGHT, KEY_CODES.ARROW_DOWN];

const DEFAULT_MONTHS_TO_SHOW = 4;

// These must be in sync with the SCSS variables
const MONTH_WIDTH = 301;
// The month header (month title + weekday names) is 56px
const HEADER_HEIGHT = 56;
// A week row is 45px high (43px + 2px margin-top)
const WEEK_HEIGHT = MONTH_WIDTH / 7 + 2;

@Component({
  tag: 'som-calendar-vertical',
  styleUrl: 'som-calendar-vertical.scss',
})
export class SomCalendarVertical {
  static WIDTH = MONTH_WIDTH;

  /**
   * Own properties
   */

  hovered = false;

  hoveredDate: Date | null;

  focusAfterUpdate = false;

  focusableDate = null;

  containerEl: HTMLDivElement;

  loadPreviousEl: HTMLDivElement;

  /**
   * Host HTML element
   */

  @Element() el: HTMLElement;

  /**
   * State variables
   */

  @State() monthObject: Date = null;

  @State() minDateObject: Date = null;

  @State() maxDateObject: Date = null;

  date1Object: Date = null;

  date2Object: Date = null;

  /**
   * Public properties
   */

  @Prop() readonly = false;

  @Prop() month: string = null;

  @Watch('month')
  handleMonthChange() {
    this.updateMonth();
  }

  // Number of months that will be visible.
  @Prop() monthsNb = DEFAULT_MONTHS_TO_SHOW;

  @Prop() minDate: string = null;

  @Watch('minDate')
  handleMinDateChange() {
    this.updateMinDate();
  }

  @Prop() maxDate: string = null;

  @Watch('maxDate')
  handleMaxDateChange() {
    this.updateMaxDate();
  }

  @Prop() enableRangePicker = false;

  @Watch('enableRangePicker')
  handleRangePickerChange(newValue) {
    if (!newValue) {
      this.date2Object = null;
    }
  }

  /**
   * This date variable will hold either:
   * - The picked date when `enableRangePicker === false`
   * - The range's start date when `enableRangePicker === true`
   */
  @Prop() date1: string = null;

  @Watch('date1')
  handleDate1Change() {
    this.clearHoveredClasses();
    this.updateDate1();
  }

  // This date variable will hold the range's end date when `enableRangePicker === true`
  @Prop() date2: string = null;

  @Watch('date2')
  handleDate2Change() {
    this.clearHoveredClasses();
    this.updateDate2();
  }

  /**
   * Events section
   */

  @Event() dateChange: EventEmitter;

  /**
   * Public methods
   */

  @Method() async reset() {
    this.updateDate1();
    this.updateDate2();
    this.updateMinDate();
    this.updateMaxDate();
    this.updateMonth();

    this.monthsNb = DEFAULT_MONTHS_TO_SHOW;
    this.containerEl.scrollTop = this.loadPreviousEl.offsetHeight;
  }

  @Method() async setFocus() {
    if (!this.focusableDate) {
      // Set focusable date on the first day of the first month
      this.setFocusableDate(getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth()));
    } else {
      // Check if date is in viewable months
      const month1 = getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth());
      const month2 = getLastDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth() + this.monthsNb - 1);

      if (!isBetweenDate(this.focusableDate, month1, month2)) {
        // Set focusable date on the first day of the first month
        this.setFocusableDate(getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth()));
      }
    }
    return this.focusDate();
  }

  /**
   * Component lifecycle events
   */

  componentWillLoad() {
    this.updateDate1();
    this.updateDate2();
    this.updateMinDate();
    this.updateMaxDate();
    this.updateMonth();
  }

  componentDidLoad() {
    this.resetFocusableDate();
  }

  componentDidUpdate() {
    this.setFocusableDate(this.focusableDate);
    if (this.focusAfterUpdate) {
      this.focusAfterUpdate = false;
      this.focusDate();
    }
  }

  /**
   * Listeners
   */

  /**
   * This @Listen('keydown') will be triggered when the component has focus and a keyboard key is pressed.
   * However, during a transition (keyboard moves to another month), focus is lost by the calendar (to avoid any focus flicker).
   */
  @Listen('keydown')
  async handleKeyDown(e) {
    if (ARROW_KEY_CODES.includes(e.keyCode)) {
      // We prevent any other listener to react on the key pressed
      e.preventDefault();
      e.stopPropagation();

      const firstVisibleMonthDay = getFirstDayOfMonthWithDate(this.monthObject);
      const lastVisibleMonthDay = getLastDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth() + this.monthsNb - 1);

      if (!this.focusableDate || !isBetweenDate(this.focusableDate, firstVisibleMonthDay, lastVisibleMonthDay)) {
        // Set focusable date on the first day of the first month
        this.setFocusableDate(getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth()));
        this.focusDate();
      } else {
        const dayEl = this.getDayElement(this.focusableDate);
        if (document.activeElement !== dayEl) {
          // The day element is not already focusable, let's focus it.
          this.setFocusableDate(this.focusableDate);
          this.focusDate();
        } else {
          // The day element is focusable. Let's move according to the arrow that was hit.
          const dateToFocus = new Date(this.focusableDate);
          if (e.keyCode === KEY_CODES.ARROW_LEFT) {
            dateToFocus.setDate(dateToFocus.getDate() - 1);
          } else if (e.keyCode === KEY_CODES.ARROW_RIGHT) {
            dateToFocus.setDate(dateToFocus.getDate() + 1);
          } else if (e.keyCode === KEY_CODES.ARROW_UP) {
            dateToFocus.setDate(dateToFocus.getDate() - 7);
          } else if (e.keyCode === KEY_CODES.ARROW_DOWN) {
            dateToFocus.setDate(dateToFocus.getDate() + 7);
          }

          // Check if date is in viewable months
          const month1 = getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth());
          const month2 = getLastDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth() + this.monthsNb - 1);
          if (isBetweenDate(dateToFocus, month1, month2)) {
            // Date is viewable
            this.setFocusableDate(dateToFocus);
            this.focusDate();
          } else if (isBeforeDate(dateToFocus, month1)) {
            // We need to move to previous month
            this.focusableDate = dateToFocus;
            this.focusAfterUpdate = true;

            this.loadPreviousMonths();
          } else if (isAfterDate(dateToFocus, month2)) {
            // We need to move to next month
            this.focusableDate = dateToFocus;
            this.focusAfterUpdate = true;

            this.loadNextMonths();
          }
        }
      }
    }
  }

  /**
   * Local methods
   */

  resetFocusableDate() {
    if (this.date1Object && isBetweenDate(this.date1Object, getFirstDayOfMonthWithDate(this.monthObject), getLastDayOfMonthWithDate(this.monthObject))) {
      // Set default focusable date to date1 if given.
      this.setFocusableDate(this.date1Object);
    } else {
      const dateToFocus = this.getFirstFocusableDate(this.monthObject);
      if (dateToFocus) {
        this.setFocusableDate(dateToFocus);
      }
    }
  }

  getDayElement(date: Date) {
    return this.el.querySelector(`.dp-day-box[data-date="${getInternalFormatFromDate(date)}"]`) as HTMLDivElement;
  }

  removeFocusableDays() {
    // Remove tabindex="0" on day boxes
    const focusableDays = [].slice.call(this.el.querySelectorAll('.dp-day-box[tabindex="0"]'));
    for (let i = 0; i < focusableDays.length; i++) {
      focusableDays[i].tabIndex = -1;
    }
  }

  setFocusableDate(date) {
    this.focusableDate = date;

    // Set tabindex="-1" to all day boxes that currently have tabindex="0"
    this.removeFocusableDays();

    if (date) {
      // Set tabindex="0" to current day box
      const dayEl = this.getDayElement(date);
      if (dayEl) {
        dayEl.tabIndex = 0;
      }
    }
  }

  async focusDate(): Promise<void> {
    if (this.focusableDate) {
      const dayEl = this.getDayElement(this.focusableDate);
      if (dayEl) {
        return focusElement(dayEl);
      }
    }

    return Promise.resolve();
  }

  updateMonth() {
    this.monthObject = this.month ? getDateFromInternalFormat(this.month) : this.date1Object || new Date();
  }

  updateMinDate() {
    this.minDateObject = this.minDate ? getDateFromInternalFormat(this.minDate) : null;
  }

  updateMaxDate() {
    this.maxDateObject = this.maxDate ? getDateFromInternalFormat(this.maxDate) : null;
  }

  updateDate1() {
    this.date1Object = this.date1 ? getDateFromInternalFormat(this.date1) : null;
  }

  updateDate2() {
    this.date2Object = this.date2 ? getDateFromInternalFormat(this.date2) : null;
  }

  clearHoveredClasses() {
    // Remove hovered states
    const selectors = [
      '.dp-day-box.between',
      '.dp-day-box.gradient-end',
      '.dp-day-box.gradient-start',
      '.dp-day-box.boundary-bg',
      '.dp-day-box.boundary-bg-start',
      '.dp-day-box.boundary-bg-end',
    ];
    const hoveredDayEls = Array.prototype.slice.call(this.el.querySelectorAll(selectors.join(',')));
    for (let i = 0; i < hoveredDayEls.length; i++) {
      const hoveredDayEl = hoveredDayEls[i];
      hoveredDayEl.classList.remove('between');
      hoveredDayEl.classList.remove('gradient-end');
      hoveredDayEl.classList.remove('gradient-start');
      hoveredDayEl.classList.remove('boundary-bg');
      hoveredDayEl.classList.remove('boundary-bg-start');
      hoveredDayEl.classList.remove('boundary-bg-end');
    }
  }

  setHoveredDate(date: Date) {
    if (this.enableRangePicker) {
      this.hoveredDate = date;

      if (this.date1 && !this.date2) {
        this.clearHoveredClasses();

        if (date) {
          // First date was selected and user is hovering to choose a second date.
          const firstDayOfRenderedMonths = getFirstDayOfMonthWithDate(this.monthObject);
          const date1IsBefore = isBeforeDate(this.date1Object, firstDayOfRenderedMonths);
          const dateIter = new Date(date1IsBefore ? firstDayOfRenderedMonths : this.date1Object);
          while (!isAfterDate(dateIter, this.hoveredDate)) {
            // Make sure to start with a date displayed
            const dayEl = this.el.querySelector(`.dp-day-box[data-date="${getInternalFormatFromDate(dateIter)}"]`);

            if (!isSameDate(dateIter, this.date1Object) && !isSameDate(dateIter, date)) {
              // Day is between the two boundaries
              dayEl.classList.add('between');
            } else if (isSameDate(dateIter, this.date1Object) && !isSameDate(dateIter, date)) {
              // Day is the start boundary
              dayEl.classList.add('boundary-bg');
              dayEl.classList.add('boundary-bg-start');
            } else if (isSameDate(dateIter, date) && !isSameDate(dateIter, this.date1Object)) {
              // Day is the end boundary
              dayEl.classList.add('boundary-bg');
              dayEl.classList.add('boundary-bg-end');
            }

            // Update gradient between months
            if (dateIter.getDay() > 0 && isSameDate(dateIter, getFirstDayOfMonthWithDate(dateIter)) && isBeforeDate(this.date1Object, dateIter)) {
              // Current date is first day of the month and hovered date is in next month
              dayEl.previousElementSibling.classList.add('gradient-start');
            } else if (dateIter.getDay() < 6 && isSameDate(dateIter, getLastDayOfMonthWithDate(dateIter)) && isAfterDate(date, dateIter)) {
              // Current date is last day of the month and hovered date is in next month
              dayEl.nextElementSibling.classList.add('gradient-end');
            }

            dateIter.setDate(dateIter.getDate() + 1);
          }
        }
      }
    }
  }

  // Called when a day box is focused and a keyboard key is hit
  handleDayKeyDown(e: KeyboardEvent, date: Date) {
    if ([KEY_CODES.ENTER, KEY_CODES.SPACE].includes(e.keyCode)) {
      e.preventDefault();
      e.stopPropagation();
      this.selectDay(date);

      this.dateChange.emit({
        date1: this.date1Object,
        date2: this.date2Object,
      });
    }
  }

  // Called when a day box is clicked
  handleDayClick(e, date: Date) {
    e.preventDefault();
    e.stopPropagation();

    this.selectDay(date, false);

    this.dateChange.emit({
      date1: this.date1Object,
      date2: this.date2Object,
    });
  }

  selectDay(date, setDayFocus = true) {
    const internalDateFormat = getInternalFormatFromDate(date);

    if (this.enableRangePicker) {
      // We are in a date picker, adjust range start + range end dates
      if (!this.date2 && this.date1 && isAfterDate(date, getDateFromInternalFormat(this.date1))) {
        this.date2 = internalDateFormat;
      } else {
        this.date1 = internalDateFormat;
        this.date2 = '';
      }
    } else {
      // We are in a single date picker, let's use the range start date only.
      this.date1 = internalDateFormat;
    }

    this.setFocusableDate(date);
    if (setDayFocus) {
      this.focusDate();
    }
  }

  formatDayLabel(date) {
    return `${WEEKDAY_NAMES[date.getDay()]}, ${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  getFocusableDate(date: Date) {
    // Return min date if it is in same month
    return this.minDateObject && isBeforeDate(date, this.minDateObject) && isBetweenDate(this.minDateObject, getFirstDayOfMonthWithDate(date), getLastDayOfMonthWithDate(date))
      ? this.minDateObject
      : date;
  }

  // Returns the first focusable date in a month based on the minDate
  getFirstFocusableDate(monthDate: Date) {
    const firstDayOfViewableMonths = getFirstDayOfMonthWithDate(monthDate);
    const lastDayOfViewableMonths = getLastDayOfMonth(monthDate.getFullYear(), monthDate.getMonth() + this.monthsNb - 1);

    // Earliest date is either the min date or the first day of the first visible month
    const earliestDate = this.minDateObject && isBeforeDate(firstDayOfViewableMonths, this.minDateObject) ? this.minDateObject : firstDayOfViewableMonths;

    // We make sure the earliest date is withing visible displayed months
    if (isBetweenDate(earliestDate, firstDayOfViewableMonths, lastDayOfViewableMonths)) {
      return earliestDate;
    }

    return null;
  }

  renderMonth(monthDate: Date) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = getFirstDayOfMonthWithDate(monthDate);
    const lastDay = getLastDayOfMonthWithDate(monthDate);
    const firstWeekDayNb = firstDay.getDay();
    const lastWeekDayNb = lastDay.getDay();

    /**
     * Put all days in weeks and pad the remaining days with '0'. Ex.:
     *  [ 0,  0,  0,  1,  2,  3,  4]
     *  [ 5,  6,  7,  8,  9, 10, 11]
     *  [12, 13, 14, 15, 16, 17, 18]
     *  [19, 20, 21, 22, 23, 24, 25]
     *  [26, 27, 28, 29, 30,  0,  0]
     */
    const weeks = [];
    let weekDays = [];
    for (let i = 0; i < firstWeekDayNb; i++) {
      weekDays.push(0);
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      weekDays.push(i);
      if (weekDays.length % 7 === 0) {
        weeks.push(weekDays);
        weekDays = [];
      }
    }
    for (let i = 1; i < 7 - lastWeekDayNb; i++) {
      weekDays.push(0);
    }
    if (weekDays.length > 0) {
      weeks.push(weekDays);
    }

    return (
      <div class="dp-month" tabindex="-1">
        <div class="dp-month-name">
          {MONTH_NAMES[month]} {year}
        </div>
        {/* Weekdays names */}
        <div class="dp-weekdays-container">
          <div class="dp-weekdays">
            {WEEKDAY_NAMES.map(name => (
              <div class="dp-weekday">{name}</div>
            ))}
          </div>
        </div>
        <div class="dp-weeks">
          {weeks.map((week, weekNb) => (
            <div class="dp-week">
              {week.map((dayNb, weekDayNb) => {
                const date = new Date(year, month, dayNb);
                let isBetween = false;
                let gradientClass = '';
                if (dayNb === 0) {
                  if (this.date1Object && this.date2Object) {
                    if (weekNb === 0) {
                      // This is a padding day box on the first week
                      const lastDayOfPreviousMonth = getLastDayOfMonth(year, month - 1);
                      const firstDayOfMonth = getFirstDayOfMonth(year, month);
                      isBetween = isBetweenDate(lastDayOfPreviousMonth, this.date1Object, this.date2Object) && isBetweenDate(firstDayOfMonth, this.date1Object, this.date2Object);

                      if (isBetween && weekDayNb === firstWeekDayNb - 1) {
                        gradientClass = 'gradient-start';
                      }
                    } else {
                      // This is a padding day box on the last week
                      const lastDayOfMonth = getLastDayOfMonth(year, month);
                      const firstDayofNextMonth = getFirstDayOfMonth(year, month + 1);
                      isBetween = isBetweenDate(lastDayOfMonth, this.date1Object, this.date2Object) && isBetweenDate(firstDayofNextMonth, this.date1Object, this.date2Object);

                      if (isBetween && weekDayNb === lastWeekDayNb + 1) {
                        gradientClass = 'gradient-end';
                      }
                    }
                  }

                  return <div class={`dp-day-box ${gradientClass}`}></div>;
                }

                isBetween = this.date1Object && this.date2Object && isBetweenDate(date, this.date1Object, this.date2Object);
                const isStartSelection = this.date1Object && isSameDate(this.date1Object, date);
                const isEndSelection = this.date2Object && isSameDate(this.date2Object, date);
                const isSelected = isStartSelection || isEndSelection;
                const isDisabled = (this.minDateObject && isBeforeDate(date, this.minDateObject)) || (this.maxDateObject && isAfterDate(date, this.maxDateObject));

                let className = '';
                let hoverableClassName = 'hoverable-border';
                if (isSelected) {
                  className = 'boundary';
                  hoverableClassName = 'hoverable-fill';
                } else {
                  if (isBetween) {
                    className = 'between';
                  }

                  if (this.enableRangePicker && this.date1Object && !this.date2Object) {
                    if (isAfterDate(date, this.date1Object)) {
                      hoverableClassName = 'hoverable-fill';
                    }
                  }
                }

                if (this.readonly) {
                  // Remove hoverable class when calendar is read only
                  hoverableClassName = '';
                }

                if (isSelected && this.date1Object && this.date2Object && isAfterDate(this.date2Object, this.date1Object)) {
                  className = `${className} boundary-bg-${isStartSelection ? 'start' : 'end'}`;
                }

                const classes = `dp-day-box ${className} ${isDisabled ? 'disabled' : hoverableClassName}`;
                return isDisabled ? (
                  <div class={classes} data-date={getInternalFormatFromDate(date)}>
                    <div class="dp-day" tabindex="-1" aria-label={this.formatDayLabel(date)}>
                      <div class="dp-day-inside">{dayNb > 0 ? dayNb : ''}</div>
                    </div>
                  </div>
                ) : (
                  <div
                    class={classes}
                    onMouseEnter={() => {
                      if (!this.readonly && !isDisabled) {
                        // Update hovered state
                        this.hovered = true;
                        this.setHoveredDate(date);
                      }
                    }}
                    onMouseLeave={() => {
                      if (!this.readonly && !isDisabled) {
                        this.hovered = false;
                        runAfterNextFrameRender(() => {
                          /**
                           * We waited after the next frame render to check if we are truly hovered out (and that we did not
                           * simply move from one day to another one). This prevents a flicker.
                           */
                          if (!this.hovered) {
                            this.setHoveredDate(null);
                          }
                        });
                      }
                    }}
                    data-date={getInternalFormatFromDate(date)}
                    onKeyDown={e => {
                      if (!this.readonly) {
                        this.handleDayKeyDown(e, date);
                      }
                    }}
                    tabindex="-1"
                    role="button"
                    aria-label={this.formatDayLabel(date)}
                  >
                    <div
                      class="dp-day"
                      onClick={e => {
                        if (!this.readonly) {
                          this.handleDayClick(e, date);
                        }
                      }}
                      tabindex="-1"
                    >
                      <div class="dp-day-inside">{dayNb > 0 ? dayNb : ''}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  getContainerHeight(monthDate: Date) {
    const firstDay = getFirstDayOfMonthWithDate(monthDate);
    const lastDay = getLastDayOfMonthWithDate(monthDate);

    // Calculates the number of day boxes that are present in the month
    // including the empty boxes that pad the days at the beginning of the month.
    const totalDays = firstDay.getDay() + lastDay.getDate();
    return HEADER_HEIGHT + Math.ceil(totalDays / 7) * WEEK_HEIGHT;
  }

  renderMonths() {
    const months = [];
    for (let i = 0; i < this.monthsNb; i++) {
      const month = getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth() + i);
      months.push(this.renderMonth(month));
    }

    return months;
  }

  loadPreviousMonths() {
    const newFirstMonth = new Date(this.monthObject);
    newFirstMonth.setMonth(newFirstMonth.getMonth() - DEFAULT_MONTHS_TO_SHOW);

    this.monthObject = newFirstMonth;
    this.monthsNb = 2 * DEFAULT_MONTHS_TO_SHOW;

    // We calculate the new scroll distance baesed on current scroll value and month heights
    let scrollPos = this.containerEl.scrollTop;
    for (let i = 0; i < DEFAULT_MONTHS_TO_SHOW; i++) {
      const date = getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth() + i);
      scrollPos += this.getContainerHeight(date);
    }

    requestAnimationFrame(() => {
      // Before we actually render the new months (to make sure it happens in the same render frame),
      // we want to update the scrollTop to maek sure we are scrolled to the currently viewed months.
      this.containerEl.scrollTop = scrollPos;
    });
  }

  loadNextMonths() {
    if (this.monthsNb > DEFAULT_MONTHS_TO_SHOW) {
      // Calculate height of top months that are going to be removed after loading the new months at the bottom
      let currentDisplayedMonthsHeight = 0;
      for (let i = 0; i < DEFAULT_MONTHS_TO_SHOW; i++) {
        const date = getFirstDayOfMonth(this.monthObject.getFullYear(), this.monthObject.getMonth() + i);
        currentDisplayedMonthsHeight += this.getContainerHeight(date);
      }

      // Set new month object
      const newFirstMonth = new Date(this.monthObject);
      newFirstMonth.setMonth(newFirstMonth.getMonth() + DEFAULT_MONTHS_TO_SHOW);
      this.monthObject = newFirstMonth;

      // Make sure to update scrollTop when top months will be removed to display the same content.
      const offset = this.containerEl.scrollTop;
      requestAnimationFrame(() => {
        // Before we actually render the new months (to make sure it happens in the same render frame),
        // we want to update the scrollTop to maek sure we are scrolled to the currently viewed months.
        this.containerEl.scrollTop = offset - currentDisplayedMonthsHeight;
      });
    } else {
      // When first rendered, we only display one group of months. When user adds new months, we are displaying twice as many.
      this.monthsNb = 2 * DEFAULT_MONTHS_TO_SHOW;
    }
  }

  render() {
    return (
      <div
        ref={ref => {
          this.containerEl = ref;
        }}
        class="som-cv-container"
        tabindex="-1"
      >
        <div
          ref={ref => {
            this.loadPreviousEl = ref;
          }}
        >
          <div class="som-cv-more-button">
            <som-button size="sm" icon-position="only" icon="chevron-up" onClick={this.loadPreviousMonths.bind(this)}>
            </som-button>
          </div>
        </div>

        {this.renderMonths()}

        <div>
          <div class="som-cv-more-button">
            <som-button size="sm" icon-position="only" icon="chevron-down" onClick={this.loadNextMonths.bind(this)}>
            </som-button>
          </div>
        </div>
      </div>
    );
  }
}
